import { useState, useEffect, Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { usersAPI } from '../../api/users'

const DAYS  = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const SLOTS = ['08–12', '12–16', '16–20', '20–24']
const STORAGE_KEY = 'tariqgo-planning'

function getWeekDates() {
  const now = new Date()
  const monday = new Date(now)
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7))
  return DAYS.map((_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })
  })
}

function loadCells(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) return JSON.parse(saved) as Record<string, boolean>
  } catch { /* ignore */ }
  const init: Record<string, boolean> = {}
  SLOTS.forEach((_, si) => DAYS.forEach((_, di) => {
    init[`${si}-${di}`] = (si * 7 + di + di) % 3 !== 0
  }))
  return init
}

export default function LivreurPlanning() {
  const [cells,    setCells]    = useState<Record<string, boolean>>(loadCells)
  const [saving,   setSaving]   = useState(false)
  const [isOnline, setIsOnline] = useState(false)

  const { data: profile } = useQuery<{ isOnline?: boolean; isAvailable?: boolean }>({
    queryKey: ['profile-me'],
    queryFn: () => usersAPI.getMe(),
  })

  useEffect(() => {
    if (profile?.isOnline !== undefined) setIsOnline(profile.isOnline)
    else if (profile?.isAvailable !== undefined) setIsOnline(profile.isAvailable)
  }, [profile])

  const weekDates = getWeekDates()

  const toggle = (si: number, di: number) => {
    const key = `${si}-${di}`
    setCells(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const activeCount = Object.values(cells).filter(Boolean).length

  const save = async () => {
    setSaving(true)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cells))
      toast.success('Planning enregistré')
    } catch {
      toast.error('Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const toggleOnline = async () => {
    try {
      await usersAPI.setAvailability(!isOnline)
      setIsOnline(prev => !prev)
      toast.success(!isOnline ? 'Vous êtes en ligne' : 'Vous êtes hors ligne')
    } catch {
      toast.error('Erreur lors de la mise à jour du statut')
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Mon planning</h1>
          <p>Choisis tes créneaux de disponibilité pour la semaine.</p>
        </div>
        <div className="ph-actions">
          <button
            className={isOnline ? 'btn btn-ghost' : 'btn btn-green'}
            onClick={toggleOnline}
          >
            {isOnline ? '🔴 Passer hors ligne' : '🟢 Aller en ligne'}
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '120px repeat(7, 1fr)', gap: 6 }}>
          <div />
          {weekDates.map((d, i) => (
            <div key={i} style={{
              fontSize: '.78rem', color: 'var(--muted)', fontWeight: 700,
              textAlign: 'center', padding: '6px 0',
              textTransform: 'uppercase', letterSpacing: '.05em',
            }}>
              {d}
            </div>
          ))}
          {SLOTS.map((s, si) => (
            <Fragment key={si}>
              <div style={{
                fontSize: '.78rem', color: 'var(--navy-800)',
                fontWeight: 700, padding: '8px 0', alignSelf: 'center',
              }}>
                {s}
              </div>
              {DAYS.map((_, di) => {
                const active = cells[`${si}-${di}`]
                return (
                  <button
                    key={`${si}-${di}`}
                    onClick={() => toggle(si, di)}
                    style={{
                      height: 60, borderRadius: 10,
                      background: active ? 'var(--green-500)' : 'var(--navy-50)',
                      color: active ? '#fff' : 'var(--muted)',
                      fontSize: '.78rem', fontWeight: 600,
                      border: 'none', cursor: 'pointer', transition: 'all .15s',
                    }}
                  >
                    {active ? 'En ligne' : 'Off'}
                  </button>
                )
              })}
            </Fragment>
          ))}
        </div>
        <div style={{
          marginTop: 18, padding: '14px 18px',
          background: 'var(--navy-50)', borderRadius: 10,
          fontSize: '.84rem', color: 'var(--muted)',
        }}>
          Semaine du {weekDates[0]} au {weekDates[6]} ·{' '}
          <b style={{ color: 'var(--navy-900)' }}>{activeCount} créneaux actifs</b>{' '}
          · Clique pour basculer un créneau · Les modifications sont sauvegardées localement.
        </div>
      </div>
    </>
  )
}
